/**
 * Performance Monitor Utility
 *
 * A lightweight utility to detect severe performance issues in React components
 * without disrupting normal application behavior.
 *
 * Features:
 * - Low overhead (sampling-based monitoring)
 * - Focuses only on severe issues (high-frequency renders)
 * - Throttled reporting to prevent log flooding
 * - Safe for both development and production
 */

import { useEffect, useRef } from "react";

import { MonitoringConfig } from "./monitoringConfig";

// Configuration - adjust these for sensitivity
const CONFIG = {
  // Only monitor a percentage of components to reduce overhead
  SAMPLE_RATE: 0.1, // 10% sampling rate

  // Number of renders in window that indicates a problem
  SEVERE_RENDER_THRESHOLD: 10,

  // Time window for counting renders (ms)
  RENDER_WINDOW: 2000, // 2 seconds

  // Minimum time between reports for the same component
  THROTTLE_REPORT_TIME: 60000, // 1 minute
};

// Storage for monitoring data
const componentRenderTimes = new Map<string, number[]>();
const lastReportTimes = new Map<string, number>();

/**
 * Deterministic sampling function that consistently samples the same components
 * based on their name/id
 */
function shouldSampleComponent(componentId: string): boolean {
  // Simple hash function for consistent sampling
  let hash = 0;
  for (let i = 0; i < componentId.length; i++) {
    hash = (hash << 5) - hash + componentId.charCodeAt(i);
    hash |= 0; // Convert to 32bit integer
  }

  // Use absolute value and modulo for sampling decision
  return Math.abs(hash % 100) < CONFIG.SAMPLE_RATE * 100;
}

/**
 * Hook to monitor component render frequency
 *
 * @param componentId - Unique identifier for the component (e.g., 'ListView-home')
 */
export function useRenderMonitor(componentId: string): void {
  // Always initialize hooks regardless of whether we'll use them
  const renderCountRef = useRef(0);

  // Check if this component should be monitored
  const isMonitoringEnabled = MonitoringConfig.ENABLE_PERFORMANCE_MONITORING;
  const shouldMonitor =
    isMonitoringEnabled && shouldSampleComponent(componentId);

  useEffect(() => {
    // Skip the monitoring logic if monitoring is disabled or this component isn't in our sample
    if (!shouldMonitor) {
      return;
    }

    const now = Date.now();
    renderCountRef.current += 1;

    // Get or initialize render times array for this component
    let renderTimes = componentRenderTimes.get(componentId) || [];

    // Add current render time
    renderTimes.push(now);

    // Keep only renders within our monitoring window
    renderTimes = renderTimes.filter(
      (time) => now - time < CONFIG.RENDER_WINDOW
    );
    componentRenderTimes.set(componentId, renderTimes);

    // Check if we have a severe render issue
    if (renderTimes.length >= CONFIG.SEVERE_RENDER_THRESHOLD) {
      const lastReportTime = lastReportTimes.get(componentId) || 0;

      // Only report if we haven't reported recently
      if (now - lastReportTime > CONFIG.THROTTLE_REPORT_TIME) {
        const rendersPerSecond =
          (renderTimes.length / CONFIG.RENDER_WINDOW) * 1000;

        console.warn(
          `🔥 PERFORMANCE ISSUE: ${componentId} rendered ${
            renderTimes.length
          } times in ${CONFIG.RENDER_WINDOW / 1000}s`,
          `(~${rendersPerSecond.toFixed(
            1
          )} renders/sec, total lifetime renders: ${renderCountRef.current})`
        );

        // Update last report time
        lastReportTimes.set(componentId, now);
      }
    }
  });
}

/**
 * Utility to monitor Redux action frequency
 *
 * @param actionScope - A category/scope name for grouping related actions
 * @returns A function to call when an action is dispatched
 */
export function createActionMonitor(actionScope: string) {
  // Check if performance monitoring is globally disabled
  if (!MonitoringConfig.ENABLE_PERFORMANCE_MONITORING) {
    return () => {}; // Return a no-op function
  }

  // Only create monitoring for sampled scopes
  const shouldMonitor = shouldSampleComponent(actionScope);

  // If not monitoring this scope, return a no-op function
  if (!shouldMonitor) {
    return () => {};
  }

  const actionTimes = new Map<string, number[]>();
  const actionReportTimes = new Map<string, number>();

  return (actionType: string) => {
    const now = Date.now();
    const actionKey = `${actionScope}:${actionType}`;

    // Get or initialize action times array
    let times = actionTimes.get(actionKey) || [];
    times.push(now);

    // Keep only actions within our monitoring window
    times = times.filter((time) => now - time < CONFIG.RENDER_WINDOW);
    actionTimes.set(actionKey, times);

    // Check if we have a severe action frequency issue
    if (times.length >= CONFIG.SEVERE_RENDER_THRESHOLD) {
      const lastReportTime = actionReportTimes.get(actionKey) || 0;

      // Only report if we haven't reported recently
      if (now - lastReportTime > CONFIG.THROTTLE_REPORT_TIME) {
        const actionsPerSecond = (times.length / CONFIG.RENDER_WINDOW) * 1000;

        console.warn(
          `🔥 REDUX ACTION STORM: Action ${actionKey} dispatched ${
            times.length
          } times in ${CONFIG.RENDER_WINDOW / 1000}s`,
          `(~${actionsPerSecond.toFixed(1)} actions/sec)`
        );

        // Update last report time
        actionReportTimes.set(actionKey, now);
      }
    }
  };
}

export default {
  useRenderMonitor,
  createActionMonitor,
};
